import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import { Alert, Button, Flex, Form, InputNumber, Radio, Space, Switch } from 'antd';
import { CloseCircleOutlined, ExportOutlined, UndoOutlined } from '@ant-design/icons';
import useHelpers from '../../hooks/context/useHelpers';
import { BillingMode } from '../../services/exports/Constants';
import usePermissions, { ManagerPermission } from '../../hooks/auth/usePermissions';
import { Stringable } from '../../services/helpers/Stringable';
import DeleteCancelModal from '../../modals/DeleteCancelModal';
import useApiClient from '../../hooks/api/useApiClient';
import { useCancelSubscriptionMutation, useResumeSubscriptionMutation } from '../../store/api-slices';
import SubscriptionTags from './SubscriptionTags';

export default function Subscription({
  data,
  billingMode,
  setBillingMode,
  hasTrial,
  setHasTrial,
  trialDays,
  setTrialDays,
  isDraft = false,
  cancellable = true,
  onEdit,
  onSave,
  className,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Billing:Subscription' });

  const { company } = useSelector((store) => store.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { SubscriptionsManager } = useApiClient();
  const { can, cant } = usePermissions();
  const { formatCurrency, formatDate } = useHelpers();
  const [cancelSubscription, { isLoading: cancellingSubscription }] = useCancelSubscriptionMutation();
  const [resumeSubscription, { isLoading: resumingSubscription }] = useResumeSubscriptionMutation();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  const isLive = company.live_from !== null;
  const taxRate = serviceProvider?.regional_config?.tax_percentage ?? 0;

  const hasSalesBasedItems = useMemo(
    () => !!collect(data.items).first((item) => item.price?.tiers?.length > 0 || item.price_data?.tiers?.length > 0),
    [data.items],
  );

  const onCancelSubscription = async () => {
    const { data: responseData, error } = await cancelSubscription({ id: data.id, apiManager: SubscriptionsManager });

    if (error) {
      return toast.error(t('toasts.failed_to_cancel_subscription'));
    }

    setShowCancelModal(false);
    onSave && onSave(responseData.data);
  };

  const onResumeSubscription = async () => {
    const { data: responseData, error } = await resumeSubscription({ id: data.id, apiManager: SubscriptionsManager });

    if (error) {
      return toast.error(t('toasts.failed_to_resume_subscription'));
    }

    setShowResumeModal(false);
    onSave && onSave(responseData.data);
  };

  const onCancel = () => {
    if (can(ManagerPermission.CancelSubscriptions)) {
      return setShowCancelModal(true);
    }

    toast.warning(t('toasts.cancel_subscription', { provider: serviceProvider.name }), { autoClose: false });
  };

  const onResume = () => {
    if (can(ManagerPermission.CancelSubscriptions)) {
      return setShowResumeModal(true);
    }

    toast.warning(t('toasts.resume_subscription', { provider: serviceProvider.name }), { autoClose: false });
  };

  const renderStripeLink = () => {
    if (isDraft || cant(ManagerPermission.AccessPaymentGatewayDashboards)) {
      return null;
    }

    const url = new Stringable('https://dashboard.stripe.com')
      .when(import.meta.env.DEV, (value) => value.append('/test'))
      .append(`/subscriptions/${data.external_id}`)
      .toString();

    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="ml-xs">
        <ExportOutlined />
      </a>
    );
  };

  const billingModeOptions = useMemo(
    () =>
      collect(Object.values(BillingMode))
        .when(cant(ManagerPermission.CreateInternalInvoiceSubscriptions), (collection) =>
          collection.filter((item) => item !== BillingMode.InternalInvoicing),
        )
        .map((item) => (
          <Radio key={`subscription-billing-mode-${item}`} value={item}>
            {t(`billing_modes.${item}`)}
          </Radio>
        ))
        .toArray(),
    [cant],
  );

  return (
    <div className={className}>
      <div className="flex justify-between">
        <h4>
          {isDraft ? t('titles.draft') : t('titles.main')} {renderStripeLink()}
        </h4>
        {!isDraft && <SubscriptionTags subscription={data} />}
      </div>
      {isDraft && hasSalesBasedItems && (
        <Alert type="warning" showIcon title={t('labels.sales_based_discount')} className="mt-md" />
      )}
      <table className="w-full table border-none mt-md [&_th]:text-left">
        <thead>
          <tr>
            <th>{t('fields.product')}</th>
            <th>{t('fields.quantity')}</th>
            <th>{t('fields.price')}</th>
            <th>{t('fields.tax')}</th>
            <th>{t('fields.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item, index) => (
            <tr key={`subscription-item-${index}`}>
              <td>{item.product.name}</td>
              <td>{item.quantity}</td>
              <td>{formatCurrency(item.amount)}</td>
              <td>{item.price.tax_percentage} %</td>
              <td>{formatCurrency(item.taxed_amount)}</td>
            </tr>
          ))}
          <tr>
            <td />
            <td>{t('fields.net')}</td>
            <td />
            <td />
            <td>{formatCurrency(data.amount)}</td>
          </tr>
          {data?.tax > 0 && (
            <tr>
              <td />
              <td>{t('fields.tax_percentage', { rate: taxRate })}</td>
              <td />
              <td />
              <td>{formatCurrency(data.tax)}</td>
            </tr>
          )}
          <tr>
            <td />
            <td className="font-semibold">{t('fields.total')}</td>
            <td />
            <td />
            <td className="font-semibold">{formatCurrency(data.taxed_amount)}</td>
          </tr>
        </tbody>
      </table>
      {can([ManagerPermission.EditSubscriptionPrices, ManagerPermission.ManageSubscriptionTrial]) && isDraft && (
        <div>
          <hr className="my-6" />
          <h5 className="mb-md">{t('titles.billing_settings')}</h5>
          <Form layout="vertical">
            <Form.Item label={t('form.billing_mode.label')} required>
              <Radio.Group onChange={(e) => setBillingMode(e.target.value)} defaultValue={billingMode} size="large">
                <Space orientation="vertical">{billingModeOptions}</Space>
              </Radio.Group>
            </Form.Item>
            {can(ManagerPermission.ManageSubscriptionTrial) && (
              <>
                <Form.Item>
                  <Switch checked={hasTrial} onChange={(checked) => setHasTrial(checked)} id="has_trial" />
                  <label htmlFor="has_trial" className="ml-md">
                    {t('form.has_trial.label')}
                  </label>
                </Form.Item>
                {hasTrial && (
                  <Form.Item
                    label={t('form.trial_days.label')}
                    name="trial_days"
                    required
                    rules={[
                      { required: true, message: t('form.trial_days.validation.required') },
                      {
                        validator: (_, value) =>
                          value >= 0
                            ? Promise.resolve()
                            : Promise.reject(new Error(t('form.trial_days.validation.min', { value: 0 }))),
                      },
                      {
                        validator: (_, value) =>
                          can(ManagerPermission.SetUnlimitedSubscriptionTrial) || value <= 60
                            ? Promise.resolve()
                            : Promise.reject(new Error(t('form.trial_days.validation.max', { value: 60 }))),
                      },
                    ]}
                  >
                    <InputNumber
                      value={trialDays}
                      defaultValue={trialDays}
                      placeholder={t('form.trial_days.placeholder')}
                      onChange={(value) => setTrialDays(value)}
                    />
                  </Form.Item>
                )}
              </>
            )}
          </Form>
        </div>
      )}
      {!isDraft && (
        <div className="flex justify-between">
          <div>
            <p className="text-sm text-gray-500">
              <Trans t={t} values={{ date: formatDate(data.created_at), manager: data.created_by }}>
                fields.created_at
              </Trans>
            </p>
            {Boolean(data.last_updated_by) && (
              <p className="text-sm text-gray-500">
                <Trans t={t} values={{ date: formatDate(data.updated_at), manager: data.last_updated_by }}>
                  fields.updated_at
                </Trans>
              </p>
            )}
            {data.is_on_trial && isLive && (
              <p className="text-sm text-gray-500">
                <Trans t={t} values={{ date: formatDate(data.trial_ends_at) }}>
                  fields.trial_ends_at
                </Trans>
              </p>
            )}
            {data.trial_days && !isLive ? (
              <p className="text-sm text-gray-500">{t('fields.trial_days', { trialDays: data.trial_days })}</p>
            ) : null}
            {data.is_resumable && (
              <p className="text-sm text-gray-500">
                <Trans t={t} values={{ date: formatDate(data.ends_at) }}>
                  fields.resumable_until
                </Trans>
              </p>
            )}
            {!data.is_active && (
              <p className="text-sm text-gray-500">
                <Trans t={t} values={{ date: formatDate(data.ends_at) }}>
                  fields.ended_at
                </Trans>
              </p>
            )}
            {data.is_active &&
              isLive &&
              !data.is_resumable &&
              data.next_billing_date &&
              // Off the billing cycle, internal invoicing only knows its last charge.
              (data.billing_mode === BillingMode.Stripe || data.is_cycle_aligned) && (
                <p className="text-sm text-gray-500">
                  <Trans t={t} values={{ date: formatDate(data.next_billing_date) }}>
                    fields.next_bill_at
                  </Trans>
                </p>
              )}
            {cancellable && data.is_active && !data.is_resumable && (
              <div className="items-center mt-md cursor-pointer text-gray-400" onClick={onCancel}>
                <CloseCircleOutlined className="mr-xs" />
                {t('buttons.cancel')}
              </div>
            )}
            {data.is_resumable && (
              <div className="items-center mt-md cursor-pointer text-brand-primary" onClick={onResume}>
                <UndoOutlined className="mr-xs" />
                {t('buttons.resume')}
              </div>
            )}
          </div>
          {can(ManagerPermission.ManageSubscriptions) && onEdit ? (
            <Flex vertical justify="end">
              <Button variant="solid" color="default" onClick={onEdit}>
                {t('buttons.edit')}
              </Button>
            </Flex>
          ) : null}
        </div>
      )}
      {showCancelModal && (
        <DeleteCancelModal
          show
          loading={cancellingSubscription}
          toggleModal={() => setShowCancelModal((current) => !current)}
          onConfirm={onCancelSubscription}
          title={t('modals.cancel_subscription.title', { company: company?.name })}
          description={
            <Trans
              t={t}
              values={{
                total: formatCurrency(data.taxed_amount),
                // Served by the backend since B8; the anchor only until it is deployed.
                endsAt: formatDate(data.cancellation_ends_at ?? data.billing_anchor),
                products: collect(data.items)
                  .map((item) => `${item.quantity}x ${item.product.name}`)
                  .implode(', '),
              }}
            >
              {t('modals.cancel_subscription.description')}
            </Trans>
          }
        />
      )}
      {showResumeModal && (
        <DeleteCancelModal
          show
          loading={resumingSubscription}
          toggleModal={() => setShowResumeModal((current) => !current)}
          onConfirm={onResumeSubscription}
          title={t('modals.resume_subscription.title', { company: company?.name })}
          description={
            <Trans
              t={t}
              values={{
                total: formatCurrency(data.taxed_amount),
                nextBillingDate: formatDate(data.next_billing_date),
                products: collect(data.items)
                  .map((item) => `${item.quantity}x ${item.product.name}`)
                  .implode(', '),
              }}
            >
              {t(
                data.is_next_charge_prorated
                  ? 'modals.resume_subscription.description_prorated'
                  : 'modals.resume_subscription.description',
              )}
            </Trans>
          }
        />
      )}
    </div>
  );
}
